import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ArrowLeft, MailCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { authService } from '@/services/auth.service'
import { toast } from '@/hooks/use-toast'
import AnimatedLogo from '@/components/AnimatedLogo'
import { getErrorMessage } from '@/utils/error'

/**
 * Tela pós-cadastro: instrui o usuário a clicar no link de ativação enviado
 * por e-mail. Espelha o estado de sucesso do ForgotPassword. O e-mail vem
 * via navigation state (do SignUp).
 */
export default function CheckEmail() {
  const location = useLocation()
  const email = (location.state as { email?: string } | null)?.email
  const [isResending, setIsResending] = useState(false)

  async function handleResend() {
    if (!email) return
    setIsResending(true)
    try {
      const res = await authService.resendVerification(email)
      toast({ title: 'E-mail reenviado!', description: res.message })
    } catch (error: unknown) {
      toast({
        title: 'Erro ao reenviar',
        description: getErrorMessage(error, 'Tente novamente em instantes.'),
        variant: 'destructive',
      })
    } finally {
      setIsResending(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-background to-primary/5">
      <Card className="w-full max-w-md glass-card">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <AnimatedLogo size="lg" />
          </div>
          <div className="flex justify-center mb-4">
            <MailCheck className="w-16 h-16 text-green-500" />
          </div>
          <CardTitle className="text-2xl">Confirme seu e-mail</CardTitle>
          <CardDescription>
            {email ? (
              <>
                Enviamos um link de ativação para <strong>{email}</strong>. Clique nele para ativar
                sua conta.
              </>
            ) : (
              'Enviamos um link de ativação para o seu e-mail. Clique nele para ativar sua conta.'
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 rounded-lg p-4 text-sm text-amber-800 dark:text-amber-200">
            <p className="font-medium mb-1">📧 Verifique sua caixa de entrada</p>
            <p>
              O link de ativação expira em <strong>1 hora</strong>.
            </p>
            <p className="mt-2 text-xs">
              Não recebeu? Verifique sua pasta de spam ou reenvie abaixo.
            </p>
          </div>

          {email && (
            <Button
              variant="outline"
              className="w-full"
              onClick={handleResend}
              disabled={isResending}
            >
              {isResending ? 'Reenviando...' : 'Reenviar e-mail'}
            </Button>
          )}

          <div className="pt-4 border-t">
            <Link
              to="/auth"
              className="flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar para o login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
